import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useSeo } from "@/hooks/useSeo";
import { allArticles } from "@/mocks/ranking";
import { ArticleCard, SectionHeader } from "@/components/feature/cards";

const monthly = allArticles.filter((a) => a.group === "monthly");
const monthOrder = ["二月", "一月", "十二月"];
const tabs = ["全部", ...monthOrder.filter((m) => monthly.some((a) => a.month === m))];

export default function Monthly() {
  const [tab, setTab] = useState("全部");
  const list = monthly.filter((a) => tab === "全部" || a.month === tab);
  const blackHorse = monthly.find((a) => a.rank === 1);

  useSeo({
    title: "月度总结 - 吃瓜榜单月度瓜田与黑马",
    description:
      "吃瓜榜单月度总结专区，每月初更新当月热点复盘，从月度黑马到月度反转一站看全，帮你按月补完吃瓜进度，不错过任何潜力瓜。",
    keywords: "月度总结,月度黑马,吃瓜榜单,瓜王",
    canonicalPath: "/monthly",
    ogType: "website",
    jsonLdId: "monthly",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "月度总结 - 吃瓜榜单",
      description: "吃瓜榜单月度热点总结与月度黑马合集",
      url: "https://chiguabangdan.example/monthly",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://chiguabangdan.example/" },
          { "@type": "ListItem", position: 2, name: "月度总结", item: "https://chiguabangdan.example/monthly" },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: monthly.map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: a.title,
          url: `https://chiguabangdan.example/rank/${a.id}`,
        })),
      },
    },
  });

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl px-4 pt-24 md:px-6">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">月度总结</span>
        </nav>

        <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-accent-600">
              <i className="ri-calendar-check-line" aria-hidden="true"></i>
              Monthly Roundup
            </p>
            <h1 className="text-3xl font-black text-foreground-950 md:text-5xl">月度总结</h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              每月初更新，把上个月的瓜按热度、传播周期与后劲排出座次。除了常规复盘，每一期还会单独标记一匹「月度黑马」，不让你错过任何潜力内容。
            </p>
          </div>
          <div className="flex w-full max-w-sm items-center rounded-full bg-background-100 p-1 ring-1 ring-background-200 lg:w-auto">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  tab === t ? "bg-accent-500 font-semibold text-foreground-950" : "text-foreground-700 hover:text-accent-700"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 月度黑马 */}
      {blackHorse && tab === "全部" && (
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
          <div className="flex flex-col gap-5 overflow-hidden rounded-lg bg-gradient-to-br from-accent-500/30 via-secondary-100 to-background-50 p-6 ring-1 ring-accent-200 md:flex-row md:items-center md:p-8">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
              <i className="ri-pantone-line text-3xl" aria-hidden="true"></i>
            </span>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest text-accent-700">本月黑马 · Highlight</p>
              <h2 className="mt-1 text-xl font-black text-foreground-950 md:text-2xl">{blackHorse.title}</h2>
              <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-700">{blackHorse.excerpt}</p>
            </div>
            <Link
              to={`/rank/${blackHorse.id}`}
              className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              认识它
              <i className="ri-arrow-right-line ml-1" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-7xl px-4 pb-16 md:px-6">
        {list.length > 0 ? (
          <>
            <SectionHeader eyebrow="Timeline" title={`${tab === "全部" ? "近期瓜田" : `${tab}瓜田`}一览`} />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((a) => (
                <ArticleCard key={a.id} article={a} showRank />
              ))}
            </div>
          </>
        ) : (
          <p className="py-16 text-center text-foreground-600">该月份暂无月度总结，请切换其他月份查看。</p>
        )}
      </div>
    </Layout>
  );
}